import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import MindWiseService, { 
  type MindWiseRosterPatient,
  type MindWiseWeeklyStats 
} from "@/services/MindWiseService";
import {
  Brain,
  CheckCircle2,
  Clock,
  RefreshCw,
  Search,
  Calendar,
  Phone,
  ShieldCheck,
  ChevronRight,
  Activity
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface MindWiseRosterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectPatient?: (patientId: string) => void;
}

export const MindWiseRosterModal: React.FC<MindWiseRosterModalProps> = ({
  open,
  onOpenChange,
  onSelectPatient
}) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<MindWiseWeeklyStats>(() => MindWiseService.getWeeklyIngestionStats());
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<"ALL" | "COMPLETED" | "PENDING">("ALL");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await MindWiseService.pullPatientsFromMindWise();
      setStats(MindWiseService.getWeeklyIngestionStats());
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePatientClick = (patient: MindWiseRosterPatient) => {
    onOpenChange(false);
    if (onSelectPatient && patient.id) {
      onSelectPatient(patient.id);
    } else {
      navigate(`/patients/search?query=${encodeURIComponent(patient.last_name)}`);
    }
  };

  const filteredPatients = stats.patients.filter((p) => {
    const matchesSearch = 
      p.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.mindwise_uuid.toLowerCase().includes(searchQuery.toLowerCase());

    if (!matchesSearch) return false;
    if (filterStatus === "COMPLETED") return p.screening_status === "COMPLETED";
    if (filterStatus === "PENDING") return p.screening_status !== "COMPLETED";
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="w-[95vw] sm:max-w-5xl lg:max-w-6xl max-h-[88vh] flex flex-col p-0 overflow-hidden rounded-2xl border-blue-200/80 shadow-2xl bg-white [&>[data-slot=dialog-close]]:z-20 [&>[data-slot=dialog-close]]:text-white [&>[data-slot=dialog-close]]:hover:text-white [&>[data-slot=dialog-close]]:opacity-90 [&>[data-slot=dialog-close]]:hover:opacity-100 [&>[data-slot=dialog-close]]:bg-white/10 [&>[data-slot=dialog-close]]:hover:bg-white/25 [&>[data-slot=dialog-close]]:p-1.5 [&>[data-slot=dialog-close]]:rounded-lg [&>[data-slot=dialog-close]]:top-4 [&>[data-slot=dialog-close]]:right-4 sm:[&>[data-slot=dialog-close]]:top-5 sm:[&>[data-slot=dialog-close]]:right-5 transition-all"
        aria-describedby="mindwise-roster-desc"
      >
        {/* ── Dialog Header ── */}
        <div className="bg-gradient-to-r from-blue-900 via-indigo-950 to-slate-900 p-5 sm:p-6 text-white shrink-0 relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-8 -mr-8 w-44 h-44 bg-blue-500/10 rounded-full blur-2xl pointer-events-none" />
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 relative z-10 pr-10 sm:pr-14">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-500/20 backdrop-blur-md rounded-xl border border-blue-400/30 text-blue-300">
                <Brain className="h-6 w-6 text-blue-300" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-xl font-bold text-white tracking-tight">
                    MindWise EHR Ingested Roster
                  </DialogTitle>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Ingested This Week
                  </span>
                </div>
                <DialogDescription id="mindwise-roster-desc" className="text-xs sm:text-sm text-blue-200/80 mt-0.5">
                  Automated weekly synchronization for Cycle 40 (Sep 28 – Oct 04, 2026). 5 patients scheduled for behavioral screenings.
                </DialogDescription>
              </div>
            </div>

            <Button
              size="sm"
              variant="outline"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="bg-white/10 hover:bg-white/20 text-white border-white/20 text-xs gap-1.5 shrink-0 self-start sm:self-auto sm:mr-2"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
              {isRefreshing ? "Pulling Roster..." : "Pull Latest Roster"}
            </Button>
          </div>

          {/* ── Key Metrics Ribbon inside Header ── */}
          <div className="grid grid-cols-3 gap-2.5 sm:gap-3 mt-4 pt-4 border-t border-white/10 text-xs">
            <div className="bg-white/5 rounded-xl p-2.5 border border-white/10">
              <div className="text-blue-300 text-[11px] font-medium truncate sm:text-clip">Ingested This Week</div>
              <div className="text-base sm:text-lg font-bold text-white mt-0.5">{stats.ingestedCount} Patients</div>
            </div>
            <div className="bg-white/5 rounded-xl p-2.5 border border-white/10">
              <div className="text-emerald-300 text-[11px] font-medium truncate sm:text-clip">Completed Screenings</div>
              <div className="text-base sm:text-lg font-bold text-emerald-400 mt-0.5">{stats.completedScreenings} Patients</div>
            </div>
            <div className="bg-white/5 rounded-xl p-2.5 border border-white/10">
              <div className="text-amber-300 text-[11px] font-medium truncate sm:text-clip">Awaiting Screenings</div>
              <div className="text-base sm:text-lg font-bold text-amber-300 mt-0.5">{stats.pendingScreenings} Patients</div>
            </div>
          </div>
        </div>

        {/* ── Toolbar: Search & Filter ── */}
        <div className="p-4 bg-slate-50 border-b border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or MindWise UUID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs sm:text-sm bg-white rounded-xl border border-gray-200 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>

          <div className="flex items-center gap-1.5 self-end sm:self-auto">
            <button
              onClick={() => setFilterStatus("ALL")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                filterStatus === "ALL"
                  ? "bg-blue-600 text-white shadow-xs"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-100"
              }`}
            >
              All ({stats.patients.length})
            </button>
            <button
              onClick={() => setFilterStatus("COMPLETED")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                filterStatus === "COMPLETED"
                  ? "bg-emerald-600 text-white shadow-xs"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-100"
              }`}
            >
              Completed ({stats.completedScreenings})
            </button>
            <button
              onClick={() => setFilterStatus("PENDING")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                filterStatus === "PENDING"
                  ? "bg-amber-600 text-white shadow-xs"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-100"
              }`}
            >
              Pending ({stats.pendingScreenings})
            </button>
          </div>
        </div>

        {/* ── Scrollable Patients Table ── */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 bg-gray-50/50">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-xs">
            <table className="w-full text-left text-xs sm:text-sm">
              <thead className="bg-slate-50 border-b border-gray-200 text-gray-500 font-semibold text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4">Patient</th>
                  <th className="py-3 px-4">MindWise EHR ID</th>
                  <th className="py-3 px-4 hidden md:table-cell">Contact & DOB</th>
                  <th className="py-3 px-4">Next Visit</th>
                  <th className="py-3 px-4">Screening Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredPatients.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-gray-400">
                      No ingested patients match your filter criteria.
                    </td>
                  </tr>
                ) : (
                  filteredPatients.map((patient) => {
                    const initials = `${patient.first_name[0] || ""}${patient.last_name[0] || ""}`;
                    const visitDate = new Date(patient.next_visit).toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      year: "numeric"
                    });

                    return (
                      <tr 
                        key={patient.mindwise_uuid} 
                        className="hover:bg-blue-50/40 transition-colors group cursor-pointer"
                        onClick={() => handlePatientClick(patient)}
                      >
                        {/* Patient info */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs shrink-0">
                              {initials}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors flex items-center gap-1.5">
                                {patient.first_name} {patient.last_name}
                              </div>
                              <div className="text-[11px] text-gray-400 flex items-center gap-1 sm:hidden">
                                {patient.mindwise_uuid}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* MindWise UUID */}
                        <td className="py-3.5 px-4 font-mono text-xs">
                          <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 px-2 py-0.5 rounded font-semibold border border-slate-200">
                            {patient.mindwise_uuid}
                          </span>
                        </td>

                        {/* Contact & DOB */}
                        <td className="py-3.5 px-4 hidden md:table-cell text-xs text-gray-600">
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3 text-gray-400" />
                            {patient.mobile_number}
                          </div>
                          <div className="text-gray-400 text-[11px] mt-0.5">
                            DOB: {patient.dob}
                          </div>
                        </td>

                        {/* Next Visit */}
                        <td className="py-3.5 px-4 text-xs">
                          <div className="flex items-center gap-1.5 font-medium text-gray-800">
                            <Calendar className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                            {visitDate}
                          </div>
                          <div className="text-[11px] text-gray-400">Scheduled Visit</div>
                        </td>

                        {/* Screening Status */}
                        <td className="py-3.5 px-4">
                          {patient.screening_status === "COMPLETED" ? (
                            <div>
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-200">
                                <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                                Completed
                              </span>
                              <div className="text-[10px] text-gray-500 font-mono mt-0.5">
                                PHQ-9: {patient.phq9_score ?? "—"} · GAD-7: {patient.gad7_score ?? "—"}
                              </div>
                            </div>
                          ) : patient.screening_status === "SCHEDULED" ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                              <Clock className="h-3 w-3 text-blue-500" />
                              Scheduled
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">
                              <Activity className="h-3 w-3 text-amber-500" />
                              Awaiting Response
                            </span>
                          )}
                        </td>

                        {/* Action */}
                        <td className="py-3.5 px-4 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePatientClick(patient);
                            }}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline p-1"
                          >
                            <span>Open Profile</span>
                            <ChevronRight className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Dialog Footer ── */}
        <div className="p-4 bg-white border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-gray-500 flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            <span>Encrypted MindWise EHR Integration active for Pelham Branch · Ally Psychiatry</span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="text-xs"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MindWiseRosterModal;
